import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { palette, Icon } from '@m12s/component-library';
import routes from 'lib/utils/routes';
import styled, { keyframes } from 'styled-components';
import { zLayer } from 'lib/utils/styled';
import { falTimes } from 'lib/icons';

import { getMachine } from 'lib/selectors/getMachine';
import { getCurrentWorkOrderOp } from 'lib/selectors/getCurrentWorkOrderOp';
import { getLatestJob } from 'lib/selectors/getLatestJob';
import { getLatestActivityMode } from 'lib/selectors/getLatestActivityMode';

import {
  DRAWER_TOP_INSET,
  DRAWER_TOP_RADIUS,
  DRAWER_SHADOW,
} from 'components/MMDrawer';
import { JobDocsFooter } from 'components/JobDocsFooter';
import { useJobDocsIframe } from 'hooks/useJobDocsIframe';
import { resolveJobDocsContext } from 'lib/jobDocs/resolveJobDocsContext';
import { useScrimLayer } from 'lib/contexts/ScrimContext';

import NoOrderOpContent from './NoOrderOpContent';
import OrderOpContent from './OrderOpContent';
import { ContentContainer } from './styled';

const DOCUMENTS_EMBED_ZONE_ID = 'jobinfoerp-documents-zone';

// This overview is a routed page, not an MMDrawer, but it must LOOK (and
// enter) like one (Figma: Current Job Modal) — same top inset, rounded top
// corners, shadow and slide-up entrance, over a dimmed backdrop. The chrome
// tokens are shared with MMDrawer so the two surfaces can never drift apart.
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${zLayer.overlay};
  display: flex;
  flex-direction: column;
  background: rgba(26, 32, 44, 0.65);
  animation: ${fadeIn} 0.2s ease-out;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  margin-top: ${DRAWER_TOP_INSET};
  background: ${palette.White};
  border-radius: ${DRAWER_TOP_RADIUS} ${DRAWER_TOP_RADIUS} 0 0;
  box-shadow: ${DRAWER_SHADOW};
  overflow: hidden;
  /* Matches the Chakra bottom-drawer entrance so this drawer-like page and
     the real drawers feel identical. */
  animation: ${slideUp} 0.3s cubic-bezier(0, 0, 0.2, 1);
`;

// Figma modal header: 72px white bar, 1px Grey/400 bottom border,
// 24px left / 16px right padding.
const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  height: 72px;
  padding: 0 1rem 0 1.5rem;
  background: ${palette.White};
  border-bottom: 1px solid ${palette.Grey400};
`;

// Figma: title is Noto Sans 400 (regular), 20px/24px, Grey/700.
const HeaderTitle = styled.h5`
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 20px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.5px;
  color: ${palette.Grey700};
`;

// Figma: 40px basic icon button with the regular-weight 24px times glyph.
const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: ${palette.Grey700};
  cursor: pointer;

  &:hover {
    background: ${palette.Grey200};
  }

  &:focus-visible {
    outline: 2px solid ${palette.Blue500};
    outline-offset: 2px;
  }
`;

const JobInfoERP = ({
  handleOnClose = (history, machine, tab) => {
    // The overlay is nested under home/:tab — closing returns to the SAME
    // tab. A missing tab (stale un-nested URL) falls back to the home
    // redirect, which picks the default tab.
    return history.push({
      pathname: tab
        ? routes.machineIdHomeTabPath(machine.id, tab)
        : routes.machineIdHomePath(machine.id),
    });
  },
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { tab } = useParams();
  // This page's Backdrop dims the screen — register it as a scrim layer so
  // anything stacking above (e.g. the View Documents drawer) skips its own
  // dim and the fade keeps this backdrop's transparency.
  useScrimLayer(true);

  const job = useSelector(getLatestJob);
  const machine = useSelector(getMachine);

  const currentWorkOrderOp = useSelector(getCurrentWorkOrderOp);

  const activityMode = useSelector(getLatestActivityMode);
  const { partNumber, operationId } = resolveJobDocsContext(
    currentWorkOrderOp,
    {
      job,
    }
  );
  const {
    showSection: showJobDocsSection,
    gridUrl: jobDocsGridUrl,
    gridPath: jobDocsGridPath,
  } = useJobDocsIframe({
    partNumber,
    operationId,
    activityMode,
  });

  return (
    <Backdrop id="job-details-page">
      <Panel>
        <Header>
          <HeaderTitle>
            {!currentWorkOrderOp
              ? `${job.name} | ${t('Operation overview')}`
              : t('Current job')}
          </HeaderTitle>
          <CloseButton
            type="button"
            aria-label={t('Close')}
            onClick={() => {
              handleOnClose(history, machine, tab);
            }}
          >
            {/* falTimes (light) matches the docs drawer's DrawerHeaderBar X. */}
            <Icon icon={falTimes} size="1.5rem" />
          </CloseButton>
        </Header>
        <ContentContainer>
          {currentWorkOrderOp?.workOrderId ? (
            <OrderOpContent />
          ) : (
            <NoOrderOpContent job={job} />
          )}
        </ContentContainer>
        {/* Figma: the View Documents footer is the bottom bar of the overview —
            the single job-docs entry point on this screen. */}
        {showJobDocsSection && (
          <JobDocsFooter
            embedZoneId={DOCUMENTS_EMBED_ZONE_ID}
            gridUrl={jobDocsGridUrl}
            gridPath={jobDocsGridPath}
            partNumber={partNumber}
            operationId={operationId}
          />
        )}
      </Panel>
    </Backdrop>
  );
};

JobInfoERP.propTypes = {
  handleOnClose: PropTypes.func,
};

export default JobInfoERP;
